export const BASE_URL = `${process.env.REACT_APP_API_URL}/api/admin`;
export const BASE_URL_V1 = `${process.env.REACT_APP_API_URL}/v1/admin`;

export interface IdNameTransport {
  id: number;
  name: string;
  portfolio: {
    portfolioId: number;
    portfolioName: string;
  }
}

export class ErrorResponse {
  status: number;

  error: string;

  message: string;

  constructor(status: number, error: string, message: string) {
    this.status = status;
    this.error = error;
    this.message = message;
  }
}

export function isError() {}

export interface PageResult<T> {
  content: T[];
  totalElements: number;
  numberOfElements: number;
  totalPages: number;
  last: boolean;
  number: number; //current page
  size: number;
}

interface HttpResponse<T> extends Response {
  parsedBody?: T;
}

function getQueryParameters(
  queryParams: Map<string, string | number | boolean>
) {
  const query: string[] = [];
  queryParams.forEach((v, k) => {
    query.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  });

  return query?.join("&") || "";
}

function prepHeaders(
  method: string,
  authToken: string | null,
  body?: any,
  headers?: any
) {
  const defaultHeaders =
    body instanceof FormData
      ? {
          Accept: "application/json",
        }
      : {
          Accept: "application/json",
          "Content-Type": "application/json",
        };

  let args: RequestInit = {
    method,
    headers: {
      ...defaultHeaders,
      ...headers,
    },
  };

  if (authToken) {
    args = {
      ...args,
      headers: {
        ...args.headers,
        authorization: "Bearer " + authToken,
      },
    };
  }

  if (body instanceof FormData) {
    args.body = body;
  } else {
    args.body = JSON.stringify(body);
  }

  return args;
}

function copyHeadersToRequest(
  headers: Map<string, string> | undefined,
  request: Request
) {
  if (headers) {
    headers.forEach((v, k) => {
      request.headers.set(k, v);
    });
  }
}

export async function get<T>(
  path: string,
  authToken: string,
  queryParams?: Map<string, string | number | boolean>,
  headers?: Map<string, string>
): Promise<HttpResponse<T>> {
  const args = prepHeaders("get", authToken);

  if (queryParams) {
    path = `${path}?${getQueryParameters(queryParams)}`;
  }

  const request = new Request(path, args);
  copyHeadersToRequest(headers, request);

  return await http<T>(request);
}

export async function post<T>(
  path: string,
  authToken: string,
  queryParams?: Map<string, string | number | boolean>,
  body?: any,
  headers?: any
): Promise<HttpResponse<T | ErrorResponse>> {
  const args = prepHeaders("post", authToken, body, headers);

  if (queryParams) {
    path = `${path}?${getQueryParameters(queryParams)}`;
  }

  // prepare body

  return await http<T>(new Request(path, args));
}

export async function put<T>(
  path: string,
  authToken: string,
  queryParams?: Map<string, string | number | boolean>,
  body?: any
): Promise<HttpResponse<T | ErrorResponse>> {
  const args = prepHeaders("put", authToken, body);

  if (queryParams) {
    path = `${path}?${getQueryParameters(queryParams)}`;
  }

  // prepare body

  return await http<T>(new Request(path, args));
}

export async function deleteRequest<T>(
  path: string,
  authToken: string,
  queryParams?: Map<string, string | number | boolean>,
  body?: any
): Promise<HttpResponse<T | ErrorResponse>> {
  const args = prepHeaders("delete", authToken, body);

  if (queryParams) {
    path = `${path}?${getQueryParameters(queryParams)}`;
  }

  return await http<T>(new Request(path, args));
}

export async function http<T>(request: RequestInfo): Promise<HttpResponse<T>> {
  const response: HttpResponse<T> | void = await fetch(request);

  if (!response) {
    throw new Error("Failed to fetch");
  }

  let parsedBody: any = {};

  try {
    // may error if there is no body
    parsedBody = await response.json();
  } catch (err) {}

  response.parsedBody = parsedBody;

  return response;
}

export async function fetchFile(url: string, authToken: string) {
  return downloadFileFile(url, "GET",  authToken, null);
}

export async function postForFile(url: string, body: any | null, authToken: string) {
  return downloadFileFile(url, "POST", authToken, body);
}

async function downloadFileFile(url: string, method: "GET" | "POST", authToken: string, body: any | null) {
  let init: any = {
    method: method,
    headers: {
      Accept: "text/csv",
      "Content-Type": "application/json",
      authorization: "Bearer " + authToken,
    }
  };

  if (body != null) {
    init.body = JSON.stringify(body);
  }

  const response = await fetch(
    new Request(url, init)
  );

  const contentHeaders = response.headers.get("content-disposition");

  let filename;
  if (contentHeaders) {
    filename =  contentHeaders.split(';').length > 1 ? contentHeaders.split(";")[1].split("=")[1] : contentHeaders;
  }

  return { blob: await response.blob(), fileName: filename };
}
